import { useState } from 'react';
import {
  MessageSquare,
  FileText,
  BarChart3,
  Workflow,
  LayoutDashboard,
  ArrowRight,
  ArrowLeftRight,
  Sparkles,
  Activity,
  Clock,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/auth/AuthContext';
import { fetchConversations } from '@/api/chat';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { BrandLogo } from '@/components/BrandLogo';
import { EmployerLogo } from '@/components/EmployerLogo';
import { CompanySwitcher } from '@/components/auth/CompanySwitcher';
import { FEATURES } from '@/config/features';
import type { AITool } from './types';

export type HomeNavigateOptions = { conversationId?: number };

interface HomeViewProps {
  onNavigate: (tool: AITool, options?: HomeNavigateOptions) => void;
}

const RECENT_LIMIT = 5;

const allModules = [
  {
    id: 'chat' as AITool,
    icon: MessageSquare,
    title: 'AI Chat',
    description: 'Ask payroll questions in natural language',
    accent: 'bg-sky-500/10 text-sky-700',
  },
  {
    id: 'reports' as AITool,
    icon: BarChart3,
    title: 'Reports',
    description: 'Generate AI-powered payroll insights',
    accent: 'bg-emerald-500/10 text-emerald-700',
  },
  {
    id: 'documents' as AITool,
    icon: FileText,
    title: 'Documents',
    description: 'Extract data from CVs and contracts',
    accent: 'bg-amber-500/10 text-amber-700',
  },
  {
    id: 'workflows' as AITool,
    icon: Workflow,
    title: 'Workflows',
    description: 'Automate recurring payroll tasks',
    accent: 'bg-teal-500/10 text-teal-700',
  },
  {
    id: 'dashboard' as AITool,
    icon: LayoutDashboard,
    title: 'Dashboards',
    description: 'Build customizable KPI and chart boards',
    accent: 'bg-indigo-500/10 text-indigo-700',
  },
];

const modules = allModules.filter((m) => m.id !== 'documents' || FEATURES.documents);

function greetingForHour(hour: number): string {
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

function formatRelativeTime(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function RecentListSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full rounded-lg" />
      ))}
    </div>
  );
}

export function HomeView({ onNavigate }: HomeViewProps) {
  const { user, selectedEmployer } = useAuth();
  const [companyOpen, setCompanyOpen] = useState(false);

  const { data: chats = [], isLoading: chatsLoading } = useQuery({
    queryKey: ['chat', 'conversations'],
    queryFn: () => fetchConversations('chat'),
  });

  const { data: reportConversations = [], isLoading: reportsLoading } = useQuery({
    queryKey: ['conversations', 'report'],
    queryFn: () => fetchConversations('report'),
  });

  const recentChats = chats.slice(0, RECENT_LIMIT);
  const recentReports = reportConversations.slice(0, RECENT_LIMIT);

  const displayName =
    [user?.firstname, user?.lastname].filter(Boolean).join(' ') ||
    user?.email?.split('@')[0] ||
    'there';
  const employerDisplay =
    selectedEmployer?.shortname ?? selectedEmployer?.name ?? selectedEmployer?.code ?? null;

  const totalTokens = user?.aiUsage?.totalTokens ?? 0;
  const limit = user?.aiUsage?.limit ?? 3_000_000;
  const usagePercent = limit > 0 ? Math.min(100, (totalTokens / limit) * 100) : 0;
  const remaining = Math.max(0, limit - totalTokens);

  const greeting = greetingForHour(new Date().getHours());
  const todayLabel = new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  return (
    <>
      <ScrollArea className="h-full">
        <div className="flex min-h-full flex-col p-6 lg:p-8">
          <div className="mx-auto w-full max-w-5xl space-y-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <BrandLogo className="mb-4 h-10 w-auto max-w-[14rem] sm:h-12" />
                <p className="text-sm text-muted-foreground">{todayLabel}</p>
                <h1 className="font-display mt-1 text-3xl font-semibold tracking-tight">
                  {greeting}, {displayName}
                </h1>
                <p className="mt-2 max-w-xl text-sm text-muted-foreground">
                  Your Payroll-GPT dashboard — private AI for payroll in Mauritius.
                </p>
              </div>
              {employerDisplay && (
                <div className="inline-flex items-center gap-2 rounded-lg border bg-card px-3 py-2 text-sm">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted/60">
                    <EmployerLogo
                      hasLogo={selectedEmployer?.hasLogo}
                      employerId={selectedEmployer?.id}
                      alt={selectedEmployer?.name ?? 'Company logo'}
                      className="h-9 w-9"
                      showFallback
                      fallbackClassName="h-4 w-4"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate font-medium">{employerDisplay}</p>
                  </div>
                  <TooltipProvider delayDuration={300}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 shrink-0 text-muted-foreground"
                          onClick={() => setCompanyOpen(true)}
                          aria-label="Switch company"
                        >
                          <ArrowLeftRight className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Switch company</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription className="flex items-center gap-1.5">
                    <Activity className="h-3.5 w-3.5" />
                    AI usage this month
                  </CardDescription>
                  <CardTitle className="text-2xl tabular-nums">
                    {totalTokens.toLocaleString()}
                    <span className="ml-1 text-sm font-normal text-muted-foreground">
                      / {limit.toLocaleString()}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Progress value={usagePercent} className="h-2" />
                  <p className="text-xs text-muted-foreground">
                    {usagePercent.toFixed(1)}% used · {remaining.toLocaleString()} tokens remaining
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardDescription className="flex items-center gap-1.5">
                    <Sparkles className="h-3.5 w-3.5" />
                    Workspace
                  </CardDescription>
                  <CardTitle className="text-lg leading-snug">
                    {selectedEmployer?.name ?? 'No company selected'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground">
                    Switch company anytime from Settings or the account menu.
                  </p>
                </CardContent>
              </Card>

              <Card className="sm:col-span-2 lg:col-span-1">
                <CardHeader className="pb-2">
                  <CardDescription>Quick tip</CardDescription>
                  <CardTitle className="text-base font-medium leading-snug">
                    Try asking Chat about leave balances or NPF contributions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <button
                    type="button"
                    onClick={() => onNavigate('chat')}
                    className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                  >
                    New AI Chat
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </CardContent>
              </Card>
            </div>

            <div>
              <p className="mb-4 max-w text-sm text-muted-foreground">
                Jump into AI Chat to ask payroll questions, build Reports for insights, or set up
                Workflows to automate recurring HR and payroll tasks.
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {modules.map(({ id, icon: Icon, title, description, accent }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => onNavigate(id)}
                    className="group flex items-start gap-4 rounded-xl border bg-card p-4 text-left transition-all hover:border-primary/40 hover:bg-accent/40"
                  >
                    <div
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${accent}`}
                    >
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-medium">{title}</h3>
                        <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                      </div>
                      <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Recent activity */}
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                  <div>
                    <CardTitle className="text-base">Recent chats</CardTitle>
                    <CardDescription>Your latest conversations</CardDescription>
                  </div>
                  <button
                    type="button"
                    onClick={() => onNavigate('chat')}
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                  >
                    View all
                    <ArrowRight className="h-3 w-3" />
                  </button>
                </CardHeader>
                <CardContent>
                  {chatsLoading ? (
                    <RecentListSkeleton />
                  ) : recentChats.length === 0 ? (
                    <div className="rounded-lg border border-dashed px-4 py-8 text-center">
                      <MessageSquare className="mx-auto h-7 w-7 text-muted-foreground/50" />
                      <p className="mt-2 text-sm text-muted-foreground">No chats yet</p>
                      <button
                        type="button"
                        onClick={() => onNavigate('chat')}
                        className="mt-2 text-sm font-medium text-primary hover:underline"
                      >
                        Start a conversation
                      </button>
                    </div>
                  ) : (
                    <ul className="space-y-1">
                      {recentChats.map((chat) => (
                        <li key={chat.id}>
                          <button
                            type="button"
                            onClick={() => onNavigate('chat', { conversationId: chat.id })}
                            className="group flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-accent/60"
                          >
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-sky-500/10 text-sky-700">
                              <MessageSquare className="h-4 w-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium">
                                {chat.title?.trim() || 'New conversation'}
                              </p>
                              <p className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Clock className="h-3 w-3" />
                                {formatRelativeTime(new Date(chat.updated_at))}
                                {chat._count.chat_message > 0 && (
                                  <span>· {chat._count.chat_message} messages</span>
                                )}
                              </p>
                            </div>
                            <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                  <div>
                    <CardTitle className="text-base">Recent reports</CardTitle>
                    <CardDescription>Your latest AI reports</CardDescription>
                  </div>
                  <button
                    type="button"
                    onClick={() => onNavigate('reports')}
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                  >
                    View all
                    <ArrowRight className="h-3 w-3" />
                  </button>
                </CardHeader>
                <CardContent>
                  {reportsLoading ? (
                    <RecentListSkeleton />
                  ) : recentReports.length === 0 ? (
                    <div className="rounded-lg border border-dashed px-4 py-8 text-center">
                      <BarChart3 className="mx-auto h-7 w-7 text-muted-foreground/50" />
                      <p className="mt-2 text-sm text-muted-foreground">No reports yet</p>
                      <button
                        type="button"
                        onClick={() => onNavigate('reports')}
                        className="mt-2 text-sm font-medium text-primary hover:underline"
                      >
                        Create a report
                      </button>
                    </div>
                  ) : (
                    <ul className="space-y-1">
                      {recentReports.map((report) => (
                        <li key={report.id}>
                          <button
                            type="button"
                            onClick={() => onNavigate('reports', { conversationId: report.id })}
                            className="group flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-accent/60"
                          >
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-emerald-500/10 text-emerald-700">
                              <BarChart3 className="h-4 w-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium">
                                {report.title?.trim() || 'Untitled Report'}
                              </p>
                              <p className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Clock className="h-3 w-3" />
                                {formatRelativeTime(new Date(report.updated_at))}
                              </p>
                            </div>
                            <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </ScrollArea>
      <CompanySwitcher open={companyOpen} onOpenChange={setCompanyOpen} />
    </>
  );
}
